import { useEffect, useState } from 'react';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Textarea } from './textarea';

/** Header, in dem das Backend die Begründung einer Löschung erwartet (RBAC D.8). */
export const GRUND_HEADER = 'X-Sidigo-Reason';

export const grundHeader = (grund: string) => ({ headers: { [GRUND_HEADER]: grund } });

/**
 * Rückfrage vor einer endgültigen Löschung — mit Pflichtfeld für den Grund.
 *
 * Das Backend verlangt für endgültiges Löschen eine Begründung (RBAC D.8) und
 * lehnt ohne sie mit „reason_required" ab. Seiten, die das nicht wussten,
 * scheiterten mit einer Fehlermeldung oben rechts (T-0143).
 */
export default function LoeschenMitGrundDialog({
  offen,
  titel,
  beschreibung,
  onAbbrechen,
  onLoeschen,
  mitGrund = true,
}: Readonly<{
  offen: boolean;
  titel: string;
  beschreibung?: string;
  /** Ohne Grund nur die Rückfrage — für Einträge, deren Löschen keinen Grund braucht. */
  mitGrund?: boolean;
  onAbbrechen: () => void;
  /** Bekommt den Grund; ein Fehler bleibt im Dialog stehen. */
  onLoeschen: (grund: string) => Promise<void>;
}>) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    if (offen) {
      setGrund('');
      setFehler('');
    }
  }, [offen]);

  const loeschen = async () => {
    if (mitGrund && !grund.trim()) {
      setFehler('Bitte gib kurz an, warum gelöscht wird.');
      return;
    }
    setLaeuft(true);
    try {
      await onLoeschen(grund.trim());
    } catch (e) {
      setFehler(e instanceof Error && e.message ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Dialog open={offen} onOpenChange={(auf) => (!auf && !laeuft ? onAbbrechen() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titel}</DialogTitle>
          <DialogDescription>
            {beschreibung ||
              (mitGrund
                ? 'Das lässt sich nicht rückgängig machen. Der Grund wird im Protokoll festgehalten.'
                : 'Das lässt sich nicht rückgängig machen.')}
          </DialogDescription>
        </DialogHeader>
        {mitGrund ? (
        <div className="grid gap-1.5">
          <label htmlFor="loeschgrund" className="text-sm font-medium">
            Grund
          </label>
          <Textarea
            id="loeschgrund"
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            placeholder="z. B. veraltet, doppelt angelegt"
            rows={3}
            autoFocus
          />
          {fehler ? <p className="text-xs text-destructive">{fehler}</p> : null}
        </div>
        ) : fehler ? (
          <p className="text-xs text-destructive">{fehler}</p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onAbbrechen} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button variant="destructive" onClick={() => void loeschen()} disabled={laeuft}>
            {laeuft ? 'Wird gelöscht…' : 'Endgültig löschen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
